import React, { useEffect, useState } from "react";
import { Button } from "../Button";
import Select from "../Select";
import { SingleValue }  from 'react-select';
import { parseFullAmount, parseSmallAmount, postHeightToParent, validAmount } from "../common-helpers";
import { getAnnualSsiPension, getNestEggGoal, getProjectedSavings, getRequiredMonthlyAmount, getTargetAnnualIncome, replacementOptions, yearsOptions } from './helpers';
import './retirement-savings.css';

const initialState = {
	grossIncome: { self: "", spouse: "", combined: 0 },// step 1
	yearsToRetirement: "15",// step 1
	incomeReplacement: "0.8",// step 1
	mssIncome: { self: "", spouse: "", combined: 0 },// step 2
	mrpIncome: { self: "", spouse: "", combined: 0 },// step 2
	cola: { self: "12", spouse: "12" },// step 2
	retirementAccounts: { self: "", spouse: "", combined: 0 },// step 3
	investmentsReturn: "8",// step 3
	homeValue: "",// step 4
	remainingMortgage: "",// step 4
	cashTowardsNewHome: "",// step 4
	yearsInRetirement: "20",// step 5
	retirementStocks: "50",// step 5
	additionalAssets: "",// step 5
	hasResult: false,
	targetAnnualIncome: "",
	annualBenefits: "",
	nestEggGoal: "",
	projectedSavings: "",
	requiredMonthlySavings: "",
};

export const RetirementSavings: React.FC = () => {
	const [ {
		grossIncome,
		yearsToRetirement,
		incomeReplacement,
		mssIncome,
		mrpIncome,
		cola,
		retirementAccounts,
		investmentsReturn,
		homeValue,
		remainingMortgage,
		cashTowardsNewHome,
		yearsInRetirement,
		retirementStocks,
		additionalAssets,
		hasResult,
		targetAnnualIncome,
		annualBenefits,
		nestEggGoal,
		projectedSavings,
		requiredMonthlySavings,
	}, setCalcValues ] = useState(initialState);

	const maxInputLength = 16;

	const onColaSelfChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCalcValues((calcValues) => ({ ...calcValues, cola: { ...cola, self: e.target.value }}));
  };

	const onColaSpouseChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCalcValues((calcValues) => ({ ...calcValues, cola: { ...cola, spouse: e.target.value}}));
  };

	const onInvestmentsReturnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setCalcValues((calcValues) => ({ ...calcValues, investmentsReturn: e.target.value}));
	};

	const onRetirementStocksChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setCalcValues((calcValues) => ({ ...calcValues, retirementStocks: e.target.value}));
	};

	const onSubmit = () => {
		grossIncome.combined = Number(validAmount(grossIncome.self)) + Number(validAmount(grossIncome.spouse));
		mrpIncome.combined = Number(validAmount(mrpIncome.self)) + Number(validAmount(mrpIncome.spouse));
		mssIncome.combined = Number(validAmount(mssIncome.self)) + Number(validAmount(mssIncome.spouse));
		retirementAccounts.combined = Number(validAmount(retirementAccounts.self)) + Number(validAmount(retirementAccounts.spouse));

		const target_annual_retirement_income = getTargetAnnualIncome(grossIncome.combined, yearsToRetirement, incomeReplacement);
		setCalcValues((calcValues) => ({ ...calcValues, targetAnnualIncome: target_annual_retirement_income.toString() }));

		const annual_ssi_pension_all_gf = getAnnualSsiPension(mrpIncome, cola, mssIncome.combined, yearsToRetirement);
		setCalcValues((calcValues) => ({ ...calcValues, annualBenefits: annual_ssi_pension_all_gf.toString() }));

		const projected_savings_balance = getProjectedSavings(retirementAccounts.combined, investmentsReturn, yearsToRetirement);
		setCalcValues((calcValues) => ({ ...calcValues, projectedSavings: projected_savings_balance.toString() }));

		const nestegg_goal = getNestEggGoal(target_annual_retirement_income, annual_ssi_pension_all_gf, retirementStocks, yearsInRetirement);
		setCalcValues((calcValues) => ({ ...calcValues, nestEggGoal: nestegg_goal.toString() }));

		const reguired_monthly_amount = getRequiredMonthlyAmount(homeValue, yearsToRetirement, remainingMortgage, cashTowardsNewHome, nestegg_goal, projected_savings_balance, additionalAssets, investmentsReturn);
		setCalcValues((calcValues) => ({ ...calcValues, requiredMonthlySavings: reguired_monthly_amount.toString() }));
		setCalcValues((calcValues) => ({ ...calcValues, hasResult: true }));
	};

	const onReset = () => {
		setCalcValues({ ...initialState });
	};

  useEffect(() => {
    postHeightToParent(document.body.scrollHeight);
  }, [hasResult]);

	return (
		<div className="container__retirementSavings">
		{ hasResult ? (
			<div className="retirementSavings-results" aria-live="polite">
				<p className="retirementSavings-result-category">
					<strong>Annual Retirement Income In Future Dollars:</strong>
					<span className="retirementSavings-result-value">{ parseFullAmount(targetAnnualIncome) }</span>
				</p>
				<p className="retirementSavings-result-category">
					<strong>Annual Social Security and Pension Benefits:</strong>
					<span className="retirementSavings-result-value">{ parseFullAmount(annualBenefits) }</span>
				</p>
				<p className="retirementSavings-result-category">
					<strong>Nest-Egg Goal:</strong>
					<span className="retirementSavings-result-value">{ parseFullAmount(nestEggGoal) }</span>
				</p>
				<p className="retirementSavings-result-category">
					<strong>Projected Future Value of Current Savings: </strong>
					<span className="retirementSavings-result-value">{ parseFullAmount(projectedSavings) }</span>
				</p>
				<p className="retirementSavings-result-category">
					<strong>How Much You Should Be Saving Each Month:</strong>
					<br/>
					<span className="retirementSavings-category-field-description">This amount includes how much you are already saving plus any employer match</span>
					<span className="retirementSavings-result-value">{ parseFullAmount(requiredMonthlySavings) }</span>
				</p>
				<Button
					label="Back"
					className="default-button retirementSavings-btn"
					onClickHandler={() => setCalcValues((calcValues) => ({ ...calcValues, hasResult: false }))}
				/>
			</div>
		) : (
		<form id="retirementSavings-form">
			{/* Question 1 */}
			<div className="retirementSavings-category-title">1. How Much Income Will You Need In Retirement?</div>
			<div className="retirementSavings-category-field">
				<div className="retirementSavings-category-field-label">
					<div className="retirementSavings-category-field-title">Current gross income:</div>
				</div>
				<div className="retirementSavings-category-input-block retirementSavings-input-block_with-spouse">
					<input
						className="retirementSavings-category-field-input"
						onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
							setCalcValues((calcValues) => ({ ...calcValues, grossIncome: { ...grossIncome, self: parseSmallAmount(e.target.value, maxInputLength)}}));
						}}
						placeholder="$0.00"
						title="Your current gross income"
						type="text"
						inputMode="numeric"
						value={grossIncome.self}
					/>
					<input
						className="retirementSavings-category-field-input retirementSavings-input__spouse"
						onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
							setCalcValues((calcValues) => ({ ...calcValues, grossIncome: { ...grossIncome, spouse: parseSmallAmount(e.target.value, maxInputLength)}}));
						}}
						placeholder="$0.00"
						title="Your spouse's current gross income"
						type="text"
						inputMode="numeric"
						value={grossIncome.spouse}
					/>
					</div>
			</div>
			<div className="retirementSavings-category-field">
				<div className="retirementSavings-category-field-label">
					<div className="retirementSavings-category-field-title">Years until you begin retirement:</div>
				<div className="retirementSavings-category-field-description">Assumes both spouses retire at the same time</div>
				</div>
				<div className="retirementSavings-category-input-block">
					<Select
						id="years-to-retirement-select"
						classNames="retirementSavings-select"
						options={yearsOptions}
						defaultOption={[{label: "15", value: "15"}]}
						onChange={(option: SingleValue<{label: string, value: string} | null>) => {
							let selectedValue = option === null ? "15" : option.value;
							setCalcValues((calcValues) => ({ ...calcValues, yearsToRetirement: selectedValue }));
						}}
					/>
					</div>
			</div>
			<div className="retirementSavings-category-field">
				<div className="retirementSavings-category-field-label">
					<div className="retirementSavings-category-field-title">Percentage of current income you want to replace:</div>
					<div className="retirementSavings-category-field-description">80% is a good ballpark estimate—more if you expect to pay for your own health insurance in retirement</div>
				</div>
				<div className="retirementSavings-category-input-block">
					<Select
						id="income-replacement-select"
						classNames="retirementSavings-select"
						options={replacementOptions}
						defaultOption={[{label: "80%", value: "0.8"}]}
						onChange={(option: SingleValue<{label: string, value: string} | null>) => {
							let selectedValue = option === null ? "0.8" : option.value;
							setCalcValues((calcValues) => ({ ...calcValues, incomeReplacement: selectedValue }));
						}}
					/>
				</div>
			</div>
			{/* Question 2 */}
			<div className="retirementSavings-category-title">2. How Much Retirement Income Will You Receive from Social Security and Pensions?</div>
			<div className="retirementSavings-category-field">
				<div className="retirementSavings-category-field-label">
					<div className="retirementSavings-category-field-title">Monthly Social Security income:</div>
					<div className="retirementSavings-category-field-description">Consult the annual statement available from the Social Security Administration or calculate your benefit at www.ssa.gov in today's dollars.</div>
				</div>
				<div className="retirementSavings-category-input-block retirementSavings-input-block_with-spouse">
					<input
						className="retirementSavings-category-field-input"
						onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
							setCalcValues((calcValues) => ({ ...calcValues, mssIncome: { ...mssIncome, self: parseSmallAmount(e.target.value, maxInputLength) } }));
						}}
						placeholder="$0.00"
						title="Your monthly Social Security income"
						type="text"
						inputMode="numeric"
						value={mssIncome.self}
					/>
					<input
						className="retirementSavings-category-field-input retirementSavings-input__spouse"
						onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
							setCalcValues((calcValues) => ({ ...calcValues, mssIncome: { ...mssIncome, spouse: parseSmallAmount(e.target.value, maxInputLength) } }));
						}}
						placeholder="$0.00"
						title="Your spouse's monthly Social Security income"
						type="text"
						inputMode="numeric"
						value={mssIncome.spouse}
					/>
					</div>
			</div>
			<div className="retirementSavings-category-field">
				<div className="retirementSavings-category-field-label">
					<div className="retirementSavings-category-field-title">Monthly retirement pension income:</div>
					<div className="retirementSavings-category-field-description">Ask your benefits department for an estimate in today's dollars.</div>
				</div>
				<div className="retirementSavings-category-input-block retirementSavings-input-block_with-spouse">
					<input
						className="retirementSavings-category-field-input"
						onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
							setCalcValues((calcValues) => ({ ...calcValues, mrpIncome: { ...mrpIncome, self: parseSmallAmount(e.target.value, maxInputLength)} }));
						}}
						placeholder="$0.00"
						title="Your monthly retirement pension income"
						type="text"
						inputMode="numeric"
						value={mrpIncome.self}
					/>
					<input
						className="retirementSavings-category-field-input retirementSavings-input__spouse"
						onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
							setCalcValues((calcValues) => ({ ...calcValues, mrpIncome: { ...mrpIncome, spouse: parseSmallAmount(e.target.value, maxInputLength)} }));
						}}
						placeholder="$0.00"
						title="Your spouse's monthly retirement pension income"
						type="text"
						inputMode="numeric"
						value={mrpIncome.spouse}
					/>
					</div>
			</div>
			<div className="retirementSavings-category-field">
				<div className="retirementSavings-category-field-label">
					<div className="retirementSavings-category-field-title">Will your pension include annual cost of living adjustments (COLA)?</div>
					<div className="retirementSavings-category-field-description">Most government pensions do; most private pensions do not.</div>
				</div>
				<div className="retirementSavings-category-input-block retirementSavings-input-block_with-spouse">
					<fieldset id="pension-fixed-cola-self">
						<label className="retirementSavings-input-radio-label">
							<input
								type="radio"
								name="pension-fixed-cola-self"
								value="12"
								onChange={onColaSelfChange}
								checked={cola.self === "12"}
							/> Yes
						</label>
						<label className="retirementSavings-input-radio-label">
							<input
								type="radio"
								name="pension-fixed-cola-self"
								value="8"
								onChange={onColaSelfChange}
								checked={cola.self === "8"}
							/> No
						</label>
					</fieldset>
					<fieldset id="pension-fixed-cola-spouse">
						<label className="retirementSavings-input-radio-label">
							<input
								type="radio"
								name="pension-fixed-cola-spouse"
								value="12"
								onChange={onColaSpouseChange}
								checked={cola.spouse === "12"}
							/> Yes
						</label>
						<label className="retirementSavings-input-radio-label">
							<input
								type="radio"
								name="pension-fixed-cola-spouse"
								value="8"
								onChange={onColaSpouseChange}
								checked={cola.spouse === "8"}
							/> No
						</label>
					</fieldset>
				</div>
			</div>
			{/* Question 3 */}
			<div className="retirementSavings-category-title">3. How Much Retirement Savings Have You Already Accumulated?</div>
			<div className="retirementSavings-category-field">
				<div className="retirementSavings-category-field-label">
					<div className="retirementSavings-category-field-title">Current value of 401(k)s, IRAs and other retirement accounts:</div>
				</div>
				<div className="retirementSavings-category-input-block retirementSavings-input-block_with-spouse">
					<input
						className="retirementSavings-category-field-input"
						onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
							setCalcValues((calcValues) => ({ ...calcValues, retirementAccounts: { ...retirementAccounts, self: parseSmallAmount(e.target.value, maxInputLength)} }));
						}}
						placeholder="$0.00"
						title="Current value of your 401(k)s, IRAs and other retirement accounts"
						type="text"
						inputMode="numeric"
						value={retirementAccounts.self}
					/>
					<input
						className="retirementSavings-category-field-input retirementSavings-input__spouse"
						onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
							setCalcValues((calcValues) => ({ ...calcValues, retirementAccounts: { ...retirementAccounts, spouse: parseSmallAmount(e.target.value, maxInputLength)} }));
						}}
						placeholder="$0.00"
						title="Current value of your spouse's 401(k)s, IRAs and other retirement accounts"
						type="text"
						inputMode="numeric"
						value={retirementAccounts.spouse}
					/>
					</div>
			</div>
			<div className="retirementSavings-category-field">
				<div className="retirementSavings-category-field-label">
					<div className="retirementSavings-category-field-title">Estimated average return on investments between now and retirement:</div>
				</div>
				<div className="retirementSavings-category-input-block">
					<fieldset id="investments-return">
						<label className="retirementSavings-input-radio-label">
							<input
								type="radio"
								name="investments-return"
								value="6"
								onChange={onInvestmentsReturnChange}
								checked={investmentsReturn === "6"}
							/> 6%
						</label>
						<label className="retirementSavings-input-radio-label">
							<input
								type="radio"
								name="investments-return"
								value="8"
								onChange={onInvestmentsReturnChange}
								checked={investmentsReturn === "8"}
							/> 8%
						</label>
						<label className="retirementSavings-input-radio-label">
							<input
								type="radio"
								name="investments-return"
								value="10"
								onChange={onInvestmentsReturnChange}
								checked={investmentsReturn === "10"}
							/> 10%
						</label>
					</fieldset>
					<div className="retirementSavings-category-field-description">(6% = Conservative; 10% = Aggressive)</div>
				</div>
			</div>
			{/* Question 4 */}
			<div className="retirementSavings-category-title">4. How Much Will You Draw From Home Equity?</div>
			<div className="retirementSavings-category-field-description">If you plan to sell your home in retirement and move elsewhere, answer the following questions. If not, skip to Step 5.</div>
			<div className="retirementSavings-category-field">
				<div className="retirementSavings-category-field-label">
					<div className="retirementSavings-category-field-title">Current market value of your home:</div>
				</div>
				<div className="retirementSavings-category-input-block">
					<input
						className="retirementSavings-category-field-input"
						onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
							setCalcValues((calcValues) => ({ ...calcValues, homeValue: parseSmallAmount(e.target.value, maxInputLength) }));
						}}
						placeholder="$0.00"
						title="Current market value of your home:"
						type="text"
						inputMode="numeric"
						value={homeValue}
					/>
				</div>
			</div>
			<div className="retirementSavings-category-field">
				<div className="retirementSavings-category-field-label">
					<div className="retirementSavings-category-field-title">Remaining mortgage at retirement:</div>
				</div>
				<div className="retirementSavings-category-input-block">
					<input
						className="retirementSavings-category-field-input"
						onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
							setCalcValues((calcValues) => ({ ...calcValues, remainingMortgage: parseSmallAmount(e.target.value, maxInputLength) }));
						}}
						placeholder="$0.00"
						title="Remaining mortgage at retirement:"
						type="text"
						inputMode="numeric"
						value={remainingMortgage}
					/>
				</div>
			</div>
			<div className="retirementSavings-category-field">
				<div className="retirementSavings-category-field-label">
					<div className="retirementSavings-category-field-title">Cash you expect to use from home sale to buy a new house:</div>
				</div>
				<div className="retirementSavings-category-input-block">
					<input
						className="retirementSavings-category-field-input"
						onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
							setCalcValues((calcValues) => ({ ...calcValues, cashTowardsNewHome: parseSmallAmount(e.target.value, maxInputLength) }));
						}}
						placeholder="$0.00"
						title="Cash you expect to use from home sale to buy a new house:"
						type="text"
						inputMode="numeric"
						value={cashTowardsNewHome}
					/>
				</div>
			</div>
			{/* Question 5 */}
			<div className="retirementSavings-category-title">5. How Big of a Nest Egg Do You Need?</div>
			<div className="retirementSavings-category-field">
				<div className="retirementSavings-category-field-label">
					<div className="retirementSavings-category-field-title">Years you expect to live in retirement:</div>
					<div className="retirementSavings-category-field-description">Assume you will live until at least 90.</div>
				</div>
				<div className="retirementSavings-category-input-block">
					<Select
						id="income-replacement-select"
						classNames="retirementSavings-select"
						options={[
							{label: "20", value: "20"},
							{label: "25", value: "25"},
							{label: "30", value: "30"},
							{label: "35", value: "35"},
						]}
						defaultOption={[{label: "20", value: "20"}]}
						onChange={(option: SingleValue<{label: string, value: string} | null>) => {
							let selectedValue = option === null ? "20" : option.value;
							setCalcValues((calcValues) => ({ ...calcValues, yearsInRetirement: selectedValue }));
						}}
					/>
				</div>
			</div>
			<div className="retirementSavings-category-field">
				<div className="retirementSavings-category-field-label">
					<div className="retirementSavings-category-field-title">How much of your portfolio will be invested in stocks during your retirement?</div>
				</div>
				<div className="retirementSavings-category-input-block">
					<fieldset id="retirement-stocks">
						<label className="retirementSavings-input-radio-label">
							<input
								type="radio"
								name="retirement-stocks"
								value="35"
								onChange={onRetirementStocksChange}
								checked={retirementStocks === "35"}
							/> 35%
						</label>
						<label className="retirementSavings-input-radio-label">
							<input
								type="radio"
								name="retirement-stocks"
								value="50"
								onChange={onRetirementStocksChange}
								checked={retirementStocks === "50"}
							/> 50%
						</label>
						<label className="retirementSavings-input-radio-label">
							<input
								type="radio"
								name="retirement-stocks"
								value="65"
								onChange={onRetirementStocksChange}
								checked={retirementStocks === "65"}
							/> 65%
						</label>
					</fieldset>
					<div className="retirementSavings-category-field-description">(35% = Conservative; 65% = Aggressive)</div>
				</div>
			</div>
			<div className="retirementSavings-category-field">
				<div className="retirementSavings-category-field-label">
					<div className="retirementSavings-category-field-title">Any additional assets expected at retirement:</div>
				</div>
				<div className="retirementSavings-category-input-block">
					<input
						className="retirementSavings-category-field-input"
						onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
							setCalcValues((calcValues) => ({ ...calcValues, additionalAssets: parseSmallAmount(e.target.value, maxInputLength) }));
						}}
						placeholder="$0.00"
						title="Any additional assets expected at retirement"
						type="text"
						inputMode="numeric"
						value={additionalAssets}
					/>
				</div>
			</div>
			<Button
			label="Calculate"
			data-testid="calc-btn"
			className="primary-button retirementSavings-btn"
			onClickHandler={onSubmit}
			/>
			<Button
				label="Start over"
				className="default-button retirementSavings-btn"
				onClickHandler={onReset}
			/>
		</form>
		)}
		</div>

	);
}
